import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { CookieWrapper } from '@services/cookie.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [FormsModule, RouterLink],
  styleUrl: './login-page.component.scss',
  host: {
    class: 'flex-1 h-full block',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './login-page.component.html',
})
export class LoginPageComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);

  working: boolean = false;

  email: string =
    environment.envType === 'LOCAL' ? 'admin.portage@domaine.fr' : '';
  password: string = environment.envType === 'LOCAL' ? '@Passw0rd.123!' : '';

  invalidEmail: boolean = false;
  invalidPassword: boolean = false;

  statusLabel: string = 'Connexion !';
  statusIcon: string = 'check';

  rotating: boolean = false;

  constructor(
    private router: Router,
    private auth: AuthService,
    private toast: ToastrService,
    private cookie: CookieWrapper
  ) {}

  ngOnInit() {
    document.addEventListener('keydown', this.onKeyDown);

    if (
      this.cookie.check('portage_token') &&
      this.auth.getCurrentUser() != null
    ) {
      void this.router.navigate(['/foyers']);
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.onKeyDown);
  }

  onKeyDown = (e: KeyboardEvent) => {
    if (e.key && e.key.toLowerCase() == 'enter') {
      this.login();
    }
  };

  login() {
    if (this.working) return;

    this.working = true;

    if (this.email.length == 0) {
      this.invalidEmail = true;
      this.statusIcon = 'error';
      this.statusLabel = 'Email invalide !';

      setTimeout(() => {
        this.working = false;
        this.invalidEmail = false;
        this.invalidPassword = false;
        this.statusIcon = 'check';
        this.statusLabel = 'Connexion !';
      }, 1000);

      return;
    }

    if (this.password.length == 0) {
      this.invalidPassword = true;
      this.statusIcon = 'error';
      this.statusLabel = 'Mot de passe invalide !';

      setTimeout(() => {
        this.working = false;
        this.invalidEmail = false;
        this.invalidPassword = false;
        this.statusIcon = 'check';
        this.statusLabel = 'Connexion !';
      }, 1000);
      return;
    }

    this.invalidEmail = false;
    this.invalidPassword = false;

    this.statusLabel = 'Connexion en cours...';
    this.statusIcon = 'sync';
    this.rotating = true;

    this.auth.login(this.email, this.password).subscribe({
      next: () => {
        this.toast.success(
          'Connexion réussie ! Redirection ...',
          'Connexion réussie'
        );
        this.working = true;
        this.invalidEmail = false;
        this.invalidPassword = false;
        this.statusIcon = 'sync';
        this.statusLabel = 'Redirection en cours...';
        this.rotating = true;
        setTimeout(() => {
          this.working = false;
          this.invalidEmail = false;
          this.invalidPassword = false;
          this.statusIcon = 'check';
          this.statusLabel = 'Connexion !';
          this.rotating = false;
          void this.router.navigate(['/foyers']);
        }, 1000);
      },
      error: err => {
        const postActions = (labelErr: string) => {
          this.statusIcon = 'error';
          this.statusLabel = labelErr;
          this.working = true;
          this.rotating = false;
          this.invalidEmail = true;
          this.invalidPassword = true;

          setTimeout(() => {
            this.working = false;
            this.invalidEmail = false;
            this.invalidPassword = false;
            this.statusIcon = 'check';
            this.statusLabel = 'Connexion !';
          }, 1000);
        };

        if (err instanceof Error) {
          if (err.message === 'errors.unauthorized_access') {
            this.toast.error(
              this.translate.instant('login.toast.unauthorized_access'),
              this.translate.instant('login.toast.error_title')
            );
            postActions(
              this.translate.instant('login.toast.unauthorized_access')
            );
          } else {
            this.toast.error(
              this.translate.instant('login.toast.generic_error'),
              this.translate.instant('login.toast.error_title')
            );
            postActions(this.translate.instant('login.toast.generic_error'));
          }
          return;
        }

        if (err === 'errors.invalid_credentials') {
          this.toast.error(
            this.translate.instant('login.toast.invalid_credentials'),
            this.translate.instant('login.toast.invalid_credentials_title')
          );
          postActions(
            this.translate.instant('login.toast.invalid_credentials_title')
          );
          return;
        }

        if (err === 'errors.account_disabled') {
          this.toast.error(
            this.translate.instant('login.toast.account_disabled'),
            this.translate.instant('login.toast.account_disabled_title')
          );
          postActions(
            this.translate.instant('login.toast.account_disabled_title')
          );
          return;
        }

        this.toast.error(
          this.translate.instant('login.toast.generic_error'),
          this.translate.instant('login.toast.error_title')
        );
        postActions(this.translate.instant('login.toast.generic_error'));
      },
    });
  }
}
